import { useState, useEffect, useCallback, useRef } from 'react';
import { aiAnalysisService, AiAnalysisJob, AiAnalysisResult } from '@/services/aiAnalysis.service';
import { AiAnalysisStatus } from '@/services/types/aiAnalysis.types';
import { inspectionService, ScrapingJobStatus } from '@/services/inspection.service';

interface UseAiAnalysisProps {
  inspectionItemId: string;
  pollingInterval?: number;
}

interface UseAiAnalysisReturn {
  aiAnalysisJob: AiAnalysisJob | null;
  aiAnalysisResult: AiAnalysisResult | null;
  isLoading: boolean;
  isProcessing: boolean;
  isCompleted: boolean;
  isFailed: boolean;
  error: string | null;
  refetch: () => Promise<void>;
}

export function useAiAnalysis({
  inspectionItemId,
  pollingInterval = 10000, // 10 segundos
}: UseAiAnalysisProps): UseAiAnalysisReturn {
  const [aiAnalysisJob, setAiAnalysisJob] = useState<AiAnalysisJob | null>(null);
  const [aiAnalysisResult, setAiAnalysisResult] = useState<AiAnalysisResult | null>(null);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const triggerAttemptedRef = useRef(false);

  const fetchAiAnalysis = useCallback(async () => {
    if (!inspectionItemId) return;

    setIsLoading(true);
    setError(null);

    try {
      let job = await aiAnalysisService.getByInspectionItemIdAsync(inspectionItemId);

      // Se não existe AI job, verificar se existe scraping job completo e criar automaticamente
      if (!job && !triggerAttemptedRef.current) {
        triggerAttemptedRef.current = true;
        try {
          const scrapingJob = await inspectionService.getScrapingJobByItemId(inspectionItemId);
          if (scrapingJob && scrapingJob.status === ScrapingJobStatus.Completed && scrapingJob.id) {
            console.log('[useAiAnalysis] Scraping completo sem AI job. Disparando criação automática para scrapingJobId:', scrapingJob.id);
            const newJob = await aiAnalysisService.triggerAiAnalysis(scrapingJob.id);
            job = newJob;
          }
        } catch (triggerErr) {
          console.warn('[useAiAnalysis] Erro ao tentar criar AI job automaticamente:', triggerErr);
          // Não bloquear o fluxo se falhar
        }
      }

      setAiAnalysisJob(job);

      if (job?.status === AiAnalysisStatus.Completed) {
        // Tentar parsear do resultJson primeiro
        if (job.resultJson) {
          const parsed = aiAnalysisService.parseResult(job);
          setAiAnalysisResult(parsed);
        }
        // Se não tem resultJson, tentar pegar do campo result
        else if (job.result) {
          setAiAnalysisResult(job.result as AiAnalysisResult);
        }
        // Se nenhum dos campos está preenchido
        else {
          setAiAnalysisResult(null);
        }
      } else {
        setAiAnalysisResult(null);
      }
    } catch (err) {
      console.error('[useAiAnalysis] Erro ao buscar análise IA:', err);
      setError(err instanceof Error ? err.message : 'Erro desconhecido');
    } finally {
      setIsLoading(false);
    }
  }, [inspectionItemId]);

  // Reset trigger flag when inspectionItemId changes
  useEffect(() => {
    triggerAttemptedRef.current = false;
  }, [inspectionItemId]);

  useEffect(() => {
    fetchAiAnalysis();
  }, [fetchAiAnalysis]);

  useEffect(() => {
    const isProcessing =
      aiAnalysisJob?.status === AiAnalysisStatus.Pending ||
      aiAnalysisJob?.status === AiAnalysisStatus.Processing;

    if (!isProcessing || !inspectionItemId) return;

    const interval = setInterval(() => {
      fetchAiAnalysis();
    }, pollingInterval);

    return () => clearInterval(interval);
  }, [aiAnalysisJob?.status, inspectionItemId, fetchAiAnalysis, pollingInterval]);

  return {
    aiAnalysisJob,
    aiAnalysisResult,
    isLoading,
    isProcessing: aiAnalysisService.isProcessing(aiAnalysisJob),
    isCompleted: aiAnalysisService.isCompleted(aiAnalysisJob),
    isFailed: aiAnalysisService.isFailed(aiAnalysisJob),
    error,
    refetch: fetchAiAnalysis,
  };
}

export default useAiAnalysis;
